import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { Upload as UploadIcon } from '@mui/icons-material';
import { Evento, EventoFormProps, EventoError, TipoEvento } from '../Eventos/Evento';
import config from '../config';

const tiposEvento: { value: TipoEvento; label: string }[] = [
  { value: 'Show', label: 'Show' },
  { value: 'Taller', label: 'Taller' },
  { value: 'Festival', label: 'Festival' },
  { value: 'Feria', label: 'Feria' },
  { value: 'Actividad', label: 'Actividad' },
  { value: 'Otro', label: 'Otro' },
];

const EventoForm: React.FC<EventoFormProps> = ({ onSubmit, initialValues = {} }) => {
  const [evento, setEvento] = useState<Evento>({
    titulo: initialValues.titulo || '',
    descripcion: initialValues.descripcion || '',
    tipo: initialValues.tipo || 'Show',
    fechaInicio: initialValues.fechaInicio || null,
    fechaFin: initialValues.fechaFin || null,
    lugar: initialValues.lugar || '',
    direccion: initialValues.direccion || '',
    ciudad: initialValues.ciudad || '',
    provincia: initialValues.provincia || '',
    precio: initialValues.precio || '',
    imagenUrl: initialValues.imagenUrl || '',
    webUrl: initialValues.webUrl || '',
    ticketUrl: initialValues.ticketUrl || '',
    destacado: initialValues.destacado || false,
    lugarId: initialValues.lugarId || null,
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const uploadedUrls: string[] = [];

    for (const file of Array.from(files)) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetch(`${config.backendUrl}/api/Places/upload-image`, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(data.fileUrl);
        }
      } catch (error) {
        console.error("Error al subir la imagen:", error);
      }
    }

    setEvento((prev) => ({
      ...prev,
      imagenUrl: uploadedUrls[0],
    }));
  };

  const [errors, setErrors] = useState<EventoError>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, checked, type } = e.target as HTMLInputElement;
    setEvento(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDateChange = (name: keyof Evento) => (date: Date | null) => {
    setEvento(prev => ({ ...prev, [name]: date }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    
    if (Object.keys(validationErrors).length === 0) {
      onSubmit({
        ...evento,
        precio: evento.precio ? Number(evento.precio) : undefined
      });
    } else {
      setErrors(validationErrors);
    }
  };

  const validate = (): EventoError => {
    const newErrors: EventoError = {};
    
    if (!evento.titulo.trim()) newErrors.titulo = 'Título es requerido';
    if (!evento.descripcion.trim()) newErrors.descripcion = 'Descripción es requerida';
    if (!evento.fechaInicio) newErrors.fechaInicio = 'Fecha de inicio es requerida';
    if (!evento.lugar.trim()) newErrors.lugar = 'Lugar es requerido';
    if (!evento.direccion.trim()) newErrors.direccion = 'Dirección es requerida';
    if (!evento.ciudad.trim()) newErrors.ciudad = 'Ciudad es requerida';
    
    return newErrors;
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, margin: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        {initialValues.id ? 'Editar Evento' : 'Nuevo Evento'}
      </Typography>
      
      <form onSubmit={handleSubmit}>
        {/* Usando Grid2 para evitar problemas de tipos */}
        <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={3}>
          {/* Sección principal */}
          <Box gridColumn="span 8">
            <TextField
              fullWidth
              label="Título del evento"
              name="titulo"
              value={evento.titulo}
              onChange={handleChange}
              error={!!errors.titulo}
              helperText={errors.titulo}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Descripción"
              name="descripcion"
              value={evento.descripcion}
              onChange={handleChange}
              error={!!errors.descripcion}
              helperText={errors.descripcion}
              margin="normal"
              multiline
              rows={4}
              required
            />
            
            <TextField
              select
              fullWidth
              label="Tipo de evento"
              name="tipo"
              value={evento.tipo}
              onChange={handleChange}
              margin="normal"
            >
              {tiposEvento.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          
          {/* Sección de imagen */}
          <Box gridColumn="span 4">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {evento.imagenUrl ? (
                <img 
                  src={`${config.backendUrl}${evento.imagenUrl}`} 
                  alt="Preview" 
                  style={{ maxWidth: '100%', maxHeight: 200, marginBottom: 16 }}
                />
              ) : (
                <Box 
                  sx={{ 
                    width: '100%', 
                    height: 200, 
                    bgcolor: 'grey.100', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: 2
                  }}
                >
                  <Typography color="text.secondary">Imagen del evento</Typography>
                </Box>
              )}
              
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
              >
                Subir imagen
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>
            </Box>
          </Box>
          
          {/* Fechas */}
          <Box gridColumn="span 6">
            <DatePicker
              label="Fecha de inicio"
              value={evento.fechaInicio}
              onChange={handleDateChange('fechaInicio')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error: !!errors.fechaInicio,
                  helperText: errors.fechaInicio,
                  required: true,
                },
              }}
            />
            <TimePicker
              label="Hora de inicio"
              value={evento.fechaInicio}
              onChange={(newValue) => handleDateChange('fechaInicio')(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                },
              }}
            />
          </Box>
          
          <Box gridColumn="span 6">
            <DatePicker
              label="Fecha de fin (opcional)"
              value={evento.fechaFin}
              onChange={handleDateChange('fechaFin')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                },
              }}
            />
            <TimePicker
              label="Hora de fin (opcional)"
              value={evento.fechaFin}
              onChange={handleDateChange('fechaFin')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                },
              }}
            />
          </Box>
          
          {/* Ubicación */}
          <Box gridColumn="span 6">
            <TextField
              fullWidth
              label="Lugar"
              name="lugar"
              value={evento.lugar}
              onChange={handleChange}
              error={!!errors.lugar}
              helperText={errors.lugar}
              margin="normal"
              required
            />
          </Box>
          
          <Box gridColumn="span 6">
            <TextField
              fullWidth
              label="Dirección"
              name="direccion"
              value={evento.direccion}
              onChange={handleChange}
              error={!!errors.direccion}
              helperText={errors.direccion}
              margin="normal"
              required
            />
          </Box>
          
          <Box gridColumn="span 6">
            <TextField
              fullWidth
              label="Ciudad"
              name="ciudad"
              value={evento.ciudad}
              onChange={handleChange}
              error={!!errors.ciudad}
              helperText={errors.ciudad}
              margin="normal"
              required
            />
          </Box>
          
          <Box gridColumn="span 6">
            <TextField
              fullWidth
              label="Provincia"
              name="provincia"
              value={evento.provincia}
              onChange={handleChange}
              margin="normal"
            />
          </Box>
          
          {/* Precio y enlaces */}
          <Box gridColumn="span 4">
            <TextField
              fullWidth
              label="Precio (opcional)"
              name="precio"
              type="number"
              value={evento.precio}
              onChange={handleChange}
              margin="normal"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Box>
          
          <Box gridColumn="span 4">
            <TextField
              fullWidth
              label="Sitio web (opcional)"
              name="webUrl"
              value={evento.webUrl}
              onChange={handleChange}
              margin="normal"
            />
          </Box>
          
          <Box gridColumn="span 4">
            <TextField
              fullWidth
              label="Link a tickets (opcional)"
              name="ticketUrl"
              value={evento.ticketUrl}
              onChange={handleChange}
              margin="normal"
            />
          </Box>
          
          {/* Opciones */}
          <Box gridColumn="span 12">
            <FormControlLabel
              control={
                <Checkbox
                  name="destacado"
                  checked={evento.destacado}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label="Evento destacado"
            />
          </Box>
          
          {/* Botones */}
          <Box gridColumn="span 12">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" color="secondary">
                Cancelar
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {initialValues?.id ? 'Actualizar' : 'Crear'} Evento
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </Paper>
  );
};

export default EventoForm;